import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ICompany } from 'src/app/models/company.model';
import { CompanyService } from 'src/app/services/company.service';
import { AuthService } from 'src/app/services/auth.service';
import { ContextService } from 'src/app/services/context.service';
import { IClinicContext } from 'src/app/models/clinic-context.model';
import { SearchService } from '../../services/search.service';
import { Store } from '@ngrx/store';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faCircle, faPenToSquare, faSort, faSortDown, faSortUp, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.sass']
})
export class CompanyComponent implements OnInit, OnDestroy {
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  sortBy = 'ASC'
  orderBy = 'corporateName'
  searchTerm: string | undefined = undefined;
  companies: ICompany[] = [];
  private searchSubscription?: Subscription;
  
  isRoot = false;

  // Expor Math para o template
  Math = Math;

  // Delete modal
  showConfirmDeletePopup = false;
  companyToDelete = '';

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 1000, label: 'Todos' }
  ];

  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faCircle = faCircle;
  faDelete = faTrash;
  faPenToSquare = faPenToSquare;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faTimes = faTimes;

   constructor(
    private companyService: CompanyService, 
    private router: Router, 
    private searchService: SearchService,
    private authService: AuthService,
    private contextService: ContextService,
    private store: Store
  ) {}

  ngOnInit(): void {
    const permission = this.authService.getPermission();
    this.isRoot = permission === 'ROOT';
    
    // Carrega a lista de empresas
    this.loading = true;
    this.getCompanies(0, this.pageSize);
    
    // Inicia a escuta de pesquisa
    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      this.searchTerm = term || undefined;
      this.getCompanies(0, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  toggleSort(column: string): void {
    if (this.orderBy === column) {
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getCompanies(0, this.pageSize);
  }

  getCompanies(pageNumber: number, pageSize: number) {
    this.loading = true;
    this.currentPage = pageNumber;
    this.companyService.listCompanies(pageNumber, pageSize, this.sortBy, this.orderBy, this.searchTerm || '')
      .subscribe({
        next: (companiesPaged: { data: ICompany[]; page_info?: { current_page: number; total_pages: number; total_elements: number; elements_per_page: number } }) => {
          this.companies = companiesPaged.data;
          if (companiesPaged.page_info) {
            this.currentPage = companiesPaged.page_info.current_page;
            this.totalPages = companiesPaged.page_info.total_pages;
            this.totalElements = companiesPaged.page_info.total_elements;
          }
         
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar empresas:', error);
          this.loading = false;
        }
      });
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getCompanies(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getCompanies(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getCompanies(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getCompanies(this.totalPages - 1, this.pageSize);
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.getCompanies(0, this.pageSize);
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getCompanies(+page - 1, this.pageSize);
    }
  }

  enterClinic(company: ICompany): void {
    const context: IClinicContext = {
      id: company.id || '',
      cnpj: company.cnpj,
      name: company.corporate_name
    };
    this.contextService.setActiveClinic(context);
    this.router.navigate(['/main/v2/dashboard']);
  }

  edit(id?: string) {
    this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
      this.router.navigate(['main', 'v2', 'company', id, 'edit']);
    });
  }

  showDeleteModal(id?: string): void {
    this.showConfirmDeletePopup = true;
    this.companyToDelete = id || '';
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.companyToDelete = '';
  }

  onDelete(): void {
    if (!this.companyToDelete) return;
    
    this.loading = true;
    this.showConfirmDeletePopup = false;
    
    this.companyService.deleteCompany(this.companyToDelete)
      .subscribe({
        next: () => {
          this.companyToDelete = '';
          this.getCompanies(this.currentPage, this.pageSize);
        },
        error: (error) => {
          console.error('Erro ao deletar empresa:', error);
          this.loading = false;
          this.companyToDelete = '';
        }
      });
  }

}
